import React, { Component } from 'react';
import Link from 'next/link';
import styled, { css } from 'styled-components';
import { COLORS } from '@ertelecom/ui-react/assets/js/constants';

import { dateToScheduleFormat } from '../helpers';
import ScheduleTooltip from '../ScheduleTooltip';

const CommonChannel = styled.div`
  display: block;
  margin-bottom: 32px;
  padding-right: 48px;
  position: relative;
  width: 33.333%;

  @media only screen and (max-width: 1359px) {
    padding-right: 64px;
    width: 50%;
  }

  @media only screen and (max-width: 1023px) {
    padding-right: 0;
    width: 100%;
  }

  &:nth-child(3n) {
    padding-right: 0;

    @media only screen and (max-width: 1359px) {
      padding-right: 64px;
    }

    @media only screen and (max-width: 1023px) {
      padding-right: 0;
    }
  }

  &:nth-child(2n) {
    @media only screen and (max-width: 1359px) {
      padding-right: 0;
    }
  }

  &::after {
    background: linear-gradient(360deg, ${COLORS.white} 0%, rgba(255, 255, 255, 0) 87.72%);
    content: "";
    pointer-events: none;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    z-index: 0;
  }
`;

const ChannelDescription = styled.div`
  align-items: stretch;
  border-bottom: 1px solid ${COLORS.grey};
  display: flex;
  flex-wrap: nowrap;
  padding-bottom: 21px;
  width: 100%;
`;

const ChannelDescriptionPicture = styled.picture`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  height: 43px;
  margin-right: 16px;
  width: 64px;
`;

const ChannelDescriptionImage = styled.img`
  max-height: 90%;
  max-width: 90%;
`;

const ChannelDescriptionContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: space-between;
`;

const ChannelDescriptionLink = styled.a`
  color: ${COLORS.blue};
  margin-top: -3px;
  text-decoration: none;
`;

const ChannelDescriptionTitle = styled.span`
  box-shadow: inset 0 -3px white, inset 0 -4px ${COLORS.blue};
  display: inline;
  font: 300 20px/24px 'robotoLight', sans-serif;
`;

const ChannelDescriptionNumber = styled.div`
  color: ${COLORS.darkGrey};
  font: 300 14px/20px 'robotoLight', sans-serif;
  margin-bottom: -5px;
`;

const BroadcastContainer = styled.div`
  margin-top: 16px;
  max-height: 270px;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const BroadcastTime = styled.div`
  flex-shrink: 0;
  margin-right: 16px;
  text-align: center;
  width: 64px;
`;

const BroadcastTitle = styled.div`
  flex: 1 0;
`;

const Broadcast = styled.div`
  align-items: stretch;
  color: ${COLORS.darkGrey};
  cursor: pointer;
  display: flex;
  font: 300 16px/24px 'robotoLight', sans-serif;
  margin-top: 16px;
  position: relative;
  width: 100%;

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 80px;
  }

  ${({ broadcastStatus }) => broadcastStatus && css`
    color: #000000;
    font-family: 'robotoRegular', Arial, Helvetica, sans-serif;
    margin-top: 14px;
  `}

  ${({ broadcastStatus }) => !broadcastStatus && css`
    ${BroadcastTitle} {
      color: #000000;
    }
  `}

  &:focus,
  &:active {
    outline: none;

    ${css`
      ${BroadcastTime},
      ${BroadcastTitle} {
        color: ${COLORS.red};
      }
    `}
  }
`;

const BroadcastProgress = styled.div`
  background-color: ${COLORS.grey};
  display: block;
  height: 3px;
  margin-top: 6px;
  width: 100%;
`;

const BroadcastProgressBar = styled.div`
  background-color: ${COLORS.lightGreen};
  display: block;
  height: 100%;
  width: 0;
`;

export default class ChannelCommon extends Component {
    static defaultProps = {
        broadcasts: [],
    };

    state = {
        broadcasts: this.props.broadcasts,
        tooltipData: {},
    }

    constructor(props) {
        super(props);

        this.broadcastNode = React.createRef();
        this.broadcastTimer = null;
    }

    componentDidMount() {
        this.updateProgress();
        this.broadcastTimer = setInterval(this.updateProgress, 10000);
    }

    componentDidUpdate({ broadcasts }) {
        if (JSON.stringify(this.props.broadcasts) !== JSON.stringify(broadcasts)) {
            clearInterval(this.broadcastTimer);
            this.updateProgress();
            this.broadcastTimer = setInterval(this.updateProgress, 10000);
        }
    }

    componentWillUnmount() {
        clearInterval(this.broadcastTimer);
    }

    updateScroll() {
        const activeBroadcast = this.broadcastNode.current.querySelector('.broadcast_active');
        if (activeBroadcast === null) {
 return;
}
        this.broadcastNode.current.scrollTop = activeBroadcast.offsetTop;
    }

    updateProgress = () => {
        const nowDate = Date.now();
        const newBroadcasts = [...this.props.broadcasts];
        const commonStatus = newBroadcasts.every(broadcast => {
            broadcast.status = false;

            const broadcastStart = typeof broadcast.start === 'string' ? new Date(broadcast.start) : broadcast.start;
            const broadcastFinish = typeof broadcast.start === 'string' ? new Date(broadcast.finish) : broadcast.finish;

            if (nowDate >= broadcastStart.getTime() && nowDate < broadcastFinish.getTime()) {
                broadcast.status = true;
                broadcast.progress = ((nowDate - broadcastStart.getTime()) / (broadcast.duration * 1000) * 100).toFixed(2);
                return false;
            }

            return true;
        });

        if (commonStatus === true) {
            this.setState({ broadcasts: this.props.broadcasts });
            clearInterval(this.broadcastTimer);
        } else {
            this.setState({ broadcasts: newBroadcasts }, this.updateScroll);
        }
    }

    onBroadcastClick = (event, broadcast) => {
        const content = {
            title: broadcast.title,
            desc: broadcast.desc,
            icon: broadcast.icon,
        };

        const coordinates = {
            x: event.clientX,
            y: event.clientY,
        };

        this.tooltipOpen(content, coordinates);
    }

    tooltipOpen = (content, coordinates) => {
        this.setState({
            tooltipData: {
                isShow: true,
                content,
                container: this.props.scheduleContainer.current,
                coordinates,
            },
        });
    }

    tooltipClose = () => {
        this.setState({
            tooltipData: {
                isShow: false,
                content: null,
            },
        });
    }

    render() {
        const broadcastList = this.state.broadcasts.map((broadcast, index) => {
            let progress = null;

            if (broadcast.status === true) {
                progress = (
                    <BroadcastProgress>
                        <BroadcastProgressBar style={{ width: `${broadcast.progress}%` }}></BroadcastProgressBar>
                    </BroadcastProgress>
                );
            }

            const broadcastStart = typeof broadcast.start === 'string' ? new Date(broadcast.start) : broadcast.start;

            return (
                <Broadcast
                    broadcastStatus={broadcast.status}
                    key={broadcast.start + index}
                    className={broadcast.status === true ? 'broadcast_active' : ''}
                    onClick={(event) => this.onBroadcastClick(event, broadcast) }
                    tabIndex='0'
                >
                    <BroadcastTime>{dateToScheduleFormat(broadcastStart)}</BroadcastTime>
                    <BroadcastTitle>
                        {broadcast.title}
                        {progress}
                    </BroadcastTitle>
                </Broadcast>
            );
        });

        let tooltip = null;
        if (this.state.tooltipData.isShow) {
            tooltip = (<ScheduleTooltip tooltipClose={this.tooltipClose} {...this.state.tooltipData} />);
        }

        return (
            <CommonChannel>
                <ChannelDescription>
                    <ChannelDescriptionPicture>
                        <ChannelDescriptionImage src={this.props.logo} alt={this.props.title} />
                    </ChannelDescriptionPicture>
                    <ChannelDescriptionContent>
                        <Link
                            href={`/domru-tv/channel/_detail?id=${this.props.id}`}
                            as={`/domru-tv/channel/${this.props.id}`}
                            scroll={false}
                            passHref
                        >
                            <ChannelDescriptionLink>
                                <ChannelDescriptionTitle>{this.props.title}</ChannelDescriptionTitle>
                            </ChannelDescriptionLink>
                        </Link>
                        <ChannelDescriptionNumber>Кнопка №{this.props.button}</ChannelDescriptionNumber>
                    </ChannelDescriptionContent>
                </ChannelDescription>

                <BroadcastContainer ref={this.broadcastNode}>
                    {broadcastList}
                </BroadcastContainer>
                {tooltip}
            </CommonChannel>
        );
    }
}
